import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  FileUp,
  FileText,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Upload,
  Eye,
  Download,
  RefreshCw,
  ChevronRight,
  User,
  Stethoscope,
  ClipboardCheck,
  Shield,
  Pen,
  Send,
  Building2,
  Activity,
  FileCheck,
  FileSignature,
  Link2,
  Loader2,
  Search,
  Filter,
  Plus,
  Trash2,
  ExternalLink,
  Brain,
  Heart,
  AlertCircle,
  CheckCircle,
  Info
} from "lucide-react";

const ELIGIBILITY_STATUS = {
  eligible: { label: "Eligible", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  likely_eligible: { label: "Likely Eligible", color: "bg-blue-100 text-blue-800", icon: Info },
  possible: { label: "Possible", color: "bg-yellow-100 text-yellow-800", icon: AlertCircle },
  not_eligible: { label: "Not Eligible", color: "bg-red-100 text-red-800", icon: XCircle },
  pending_review: { label: "Pending Review", color: "bg-purple-100 text-purple-800", icon: Clock }
};

const SIGNATURE_STATUS = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800" },
  partially_signed: { label: "Partially Signed", color: "bg-blue-100 text-blue-800" },
  completed: { label: "Completed", color: "bg-green-100 text-green-800" },
  expired: { label: "Expired", color: "bg-red-100 text-red-800" },
  voided: { label: "Voided", color: "bg-gray-100 text-gray-800" }
};

export default function ReferralAdmissionDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("eligibility");
  
  // Eligibility Assessment State
  const [pendingAssessments, setPendingAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [showAssessmentDetail, setShowAssessmentDetail] = useState(false);
  
  // Document Upload State
  const [referralDocuments, setReferralDocuments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedReferralId, setSelectedReferralId] = useState("");
  const [patientName, setPatientName] = useState("");
  
  // Electronic Signature State
  const [pendingSignatures, setPendingSignatures] = useState([]);
  const [signaturePackages, setSignaturePackages] = useState([]);
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [signingPackage, setSigningPackage] = useState(null);
  const [signatureInput, setSignatureInput] = useState("");
  
  // Portal Integration State
  const [portalIntegrations, setPortalIntegrations] = useState([]);
  const [inboundReferrals, setInboundReferrals] = useState([]);
  const [showConfigPortal, setShowConfigPortal] = useState(false);
  
  // LCD Criteria Reference
  const [lcdCriteria, setLcdCriteria] = useState(null);
  
  // New Eligibility Assessment Dialog
  const [showNewAssessment, setShowNewAssessment] = useState(false);
  const [newAssessmentData, setNewAssessmentData] = useState({
    referral_id: "",
    patient_name: "",
    primary_diagnosis: "",
    icd10_codes: "",
    karnofsky_score: "",
    weight_loss_percent: "",
    albumin: "",
    adl_bathing: false,
    adl_dressing: false,
    adl_feeding: false,
    adl_transfers: false,
    adl_continence: false,
    adl_ambulation: false,
    disease_progression: false,
    recent_hospitalizations: ""
  });

  const allowedRoles = ["admin", "administrator", "staff", "nurse", "social_worker", "physician"];

  useEffect(() => {
    if (authLoading) return;
    
    if (!user || !allowedRoles.includes(user?.role?.toLowerCase())) {
      navigate("/", { replace: true });
      return;
    }
    
    loadDashboardData();
  }, [user, navigate, authLoading]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadPendingAssessments(),
        loadPendingSignatures(),
        loadPortalStatus(),
        loadLCDCriteria(),
        loadInboundReferrals()
      ]);
    } catch (error) {
      console.error("Failed to load dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingAssessments = async () => {
    try {
      const response = await api.get("/eligibility/pending-review");
      setPendingAssessments(response.data.assessments || []);
    } catch (error) {
      console.error("Failed to load assessments:", error);
    }
  };

  const loadPendingSignatures = async () => {
    try {
      const response = await api.get("/esign/pending");
      setPendingSignatures(response.data.packages || []);
    } catch (error) {
      console.error("Failed to load signatures:", error);
    }
  };

  const loadPortalStatus = async () => {
    try {
      const response = await api.get("/portal-integrations/status");
      setPortalIntegrations(response.data.integrations || []);
    } catch (error) {
      console.error("Failed to load portal status:", error);
    }
  };

  const loadLCDCriteria = async () => {
    try {
      const response = await api.get("/eligibility/lcd-criteria");
      setLcdCriteria(response.data.criteria || null);
    } catch (error) {
      console.error("Failed to load LCD criteria:", error);
    }
  };

  const loadInboundReferrals = async () => {
    try {
      const response = await api.get("/portal-integrations/inbound");
      setInboundReferrals(response.data.referrals || []);
    } catch (error) {
      console.error("Failed to load inbound referrals:", error);
    }
  };

  // Document Upload Handler
  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    if (!selectedReferralId || !patientName) {
      toast.error("Please enter Referral ID and Patient Name first");
      return;
    }
    
    setUploadingFile(true);
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append("file", files[0]);
    formData.append("referral_id", selectedReferralId);
    formData.append("patient_name", patientName);
    formData.append("document_type", "hospital_record");
    
    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);
      
      const response = await api.post("/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      toast.success("Document uploaded! Text extraction in progress...");
      
      // Poll for extraction completion
      pollDocumentStatus(response.data.document_id);
      
    } catch (error) {
      toast.error("Upload failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingFile(false);
      setUploadProgress(0);
    }
  };

  const pollDocumentStatus = async (documentId, attempts = 0) => {
    if (attempts > 20) {
      toast.info("Document processing is taking longer than expected. Check back later.");
      return;
    }
    
    try {
      const response = await api.get(`/documents/${documentId}`);
      
      if (response.data.extraction_status === "complete") {
        toast.success("Document processed! Clinical data extracted.");
        // Refresh documents list
        if (selectedReferralId) {
          const docsResponse = await api.get(`/documents/referral/${selectedReferralId}`);
          setReferralDocuments(docsResponse.data.documents || []);
        }
      } else if (response.data.extraction_status === "failed") {
        toast.error("Document extraction failed. Please try again.");
      } else {
        // Still processing, poll again
        setTimeout(() => pollDocumentStatus(documentId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error("Polling error:", error);
    }
  };

  // Eligibility Assessment Handlers
  const handleRunAssessment = async () => {
    if (!newAssessmentData.referral_id || !newAssessmentData.patient_name || !newAssessmentData.primary_diagnosis) {
      toast.error("Please fill in required fields");
      return;
    }
    
    const hospitalRecord = {
      patient_name: newAssessmentData.patient_name,
      primary_diagnosis: newAssessmentData.primary_diagnosis,
      icd10_codes: newAssessmentData.icd10_codes.split(",").map(c => c.trim()).filter(c => c),
      karnofsky_score: newAssessmentData.karnofsky_score ? parseInt(newAssessmentData.karnofsky_score) : null,
      weight_loss_percent: newAssessmentData.weight_loss_percent ? parseFloat(newAssessmentData.weight_loss_percent) : null,
      albumin: newAssessmentData.albumin ? parseFloat(newAssessmentData.albumin) : null,
      adl_bathing: newAssessmentData.adl_bathing,
      adl_dressing: newAssessmentData.adl_dressing,
      adl_feeding: newAssessmentData.adl_feeding,
      adl_transfers: newAssessmentData.adl_transfers,
      adl_continence: newAssessmentData.adl_continence,
      adl_ambulation: newAssessmentData.adl_ambulation,
      disease_progression: newAssessmentData.disease_progression,
      recent_hospitalizations: newAssessmentData.recent_hospitalizations ? parseInt(newAssessmentData.recent_hospitalizations) : 0
    };
    
    try {
      const response = await api.post("/eligibility/assess", {
        referral_id: newAssessmentData.referral_id,
        hospital_record: hospitalRecord,
        assessor_id: user.id,
        assessor_name: user.name
      });
      
      toast.success("Eligibility assessment completed!");
      setShowNewAssessment(false);
      setSelectedAssessment(response.data);
      setShowAssessmentDetail(true);
      loadPendingAssessments();
      
    } catch (error) {
      toast.error("Assessment failed: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleApproveAssessment = async (assessmentId, approved, notes = "") => {
    try {
      await api.post("/eligibility/approve", {
        eligibility_assessment_id: assessmentId,
        approved,
        reviewer_id: user.id,
        reviewer_name: user.name,
        reviewer_notes: notes
      });
      
      toast.success(approved ? "Assessment approved!" : "Assessment rejected");
      setShowAssessmentDetail(false);
      loadPendingAssessments();
      
    } catch (error) {
      toast.error("Action failed: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleGenerateCTI = async (assessmentId) => {
    try {
      const response = await api.post("/eligibility/generate-cti", {
        eligibility_assessment_id: assessmentId,
        attending_physician: selectedAssessment?.hospital_record?.physician_name || "Attending Physician",
        attending_physician_npi: "1234567890",
        hospice_physician: "Dr. Medical Director",
        hospice_physician_npi: "0987654321"
      });
      
      toast.success("CTI document generated!");
      setSelectedAssessment({ ...selectedAssessment, cti_id: response.data.id });
      
    } catch (error) {
      toast.error("CTI generation failed: " + (error.response?.data?.detail || error.message));
    }
  };

  // Signature Handlers
  const handleSignDocument = async () => {
    if (!signatureInput.trim()) {
      toast.error("Please enter your signature");
      return;
    }
    
    try {
      const response = await api.post("/esign/sign", {
        signature_package_id: signingPackage.id,
        signature_data: btoa(signatureInput), // Base64 encode
        signature_type: "typed",
        consent_statement: "I agree to sign this document electronically"
      });
      
      toast.success("Document signed successfully!");
      setShowSignDialog(false);
      setSigningPackage(null);
      setSignatureInput("");
      loadPendingSignatures();
      
    } catch (error) {
      toast.error("Signing failed: " + (error.response?.data?.detail || error.message));
    }
  };

  // Portal Integration Handlers
  const handleImportReferral = async (inboundId) => {
    try {
      const response = await api.post(`/portal-integrations/inbound/${inboundId}/import`);
      toast.success("Referral imported successfully!");
      loadInboundReferrals();
    } catch (error) {
      toast.error("Import failed: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleSyncPortal = async (portalType) => {
    try {
      await api.post(`/portal-integrations/sync/${portalType}`);
      toast.success(`Sync with ${portalType} initiated`);
    } catch (error) {
      toast.error("Sync failed: " + (error.response?.data?.detail || error.message));
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-teal-600 mb-4" />
          <p className="text-muted-foreground">Loading Referral-to-Admission Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-white/20 rounded-lg">
                  <ClipboardCheck className="h-6 w-6" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold">Referral-to-Admission System</h1>
              </div>
              <p className="text-teal-100">Comprehensive eligibility assessment, document processing & electronic signatures</p>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                onClick={() => setShowNewAssessment(true)}
                className="bg-white text-teal-600 hover:bg-teal-50"
                data-testid="new-assessment-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Assessment
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white/80 backdrop-blur border-0 shadow-sm" data-testid="pending-assessments-stat">
            <CardContent className="p-4 text-center">
              <ClipboardCheck className="h-8 w-8 mx-auto text-purple-600 mb-2" />
              <p className="text-2xl font-bold text-purple-700">{pendingAssessments.length}</p>
              <p className="text-xs text-muted-foreground">Pending Reviews</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white/80 backdrop-blur border-0 shadow-sm" data-testid="pending-signatures-stat">
            <CardContent className="p-4 text-center">
              <FileSignature className="h-8 w-8 mx-auto text-blue-600 mb-2" />
              <p className="text-2xl font-bold text-blue-700">{pendingSignatures.length}</p>
              <p className="text-xs text-muted-foreground">Awaiting Signature</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white/80 backdrop-blur border-0 shadow-sm" data-testid="inbound-referrals-stat">
            <CardContent className="p-4 text-center">
              <ExternalLink className="h-8 w-8 mx-auto text-green-600 mb-2" />
              <p className="text-2xl font-bold text-green-700">{inboundReferrals.length}</p>
              <p className="text-xs text-muted-foreground">Inbound Referrals</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white/80 backdrop-blur border-0 shadow-sm" data-testid="active-integrations-stat">
            <CardContent className="p-4 text-center">
              <Link2 className="h-8 w-8 mx-auto text-teal-600 mb-2" />
              <p className="text-2xl font-bold text-teal-700">
                {portalIntegrations.filter(p => p.status === "active").length}
              </p>
              <p className="text-xs text-muted-foreground">Active Integrations</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-white/80 backdrop-blur p-1 rounded-lg">
            <TabsTrigger value="eligibility" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
              <ClipboardCheck className="h-4 w-4 mr-2" />
              Eligibility Assessment
            </TabsTrigger>
            <TabsTrigger value="documents" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
              <FileUp className="h-4 w-4 mr-2" />
              Document Upload
            </TabsTrigger>
            <TabsTrigger value="signatures" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
              <FileSignature className="h-4 w-4 mr-2" />
              E-Signatures
            </TabsTrigger>
            <TabsTrigger value="portals" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
              <Link2 className="h-4 w-4 mr-2" />
              Portal Integrations
            </TabsTrigger>
          </TabsList>

          {/* Eligibility Assessment Tab */}
          <TabsContent value="eligibility" className="space-y-4" data-testid="eligibility-tab">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Pending Reviews */}
              <Card className="lg:col-span-2 bg-white/90 backdrop-blur">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-purple-600" />
                    Pending Reviews
                  </CardTitle>
                  <CardDescription>Eligibility assessments awaiting human review</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px]">
                    {pendingAssessments.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500/50" />
                        <p>No pending reviews</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {pendingAssessments.map((assessment) => (
                          <div 
                            key={assessment.id}
                            className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => {
                              setSelectedAssessment(assessment);
                              setShowAssessmentDetail(true);
                            }}
                            data-testid={`assessment-${assessment.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{assessment.patient_name}</p>
                                <p className="text-sm text-muted-foreground">{assessment.primary_diagnosis}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={ELIGIBILITY_STATUS[assessment.eligibility_status]?.color || "bg-gray-100"}>
                                  {ELIGIBILITY_STATUS[assessment.eligibility_status]?.label || assessment.eligibility_status}
                                </Badge>
                                <span className="text-sm text-muted-foreground">
                                  {Math.round(assessment.confidence_score * 100)}%
                                </span>
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* LCD Criteria Quick Reference */}
              <Card className="bg-white/90 backdrop-blur">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="h-5 w-5 text-teal-600" />
                    CGS LCD Criteria
                  </CardTitle>
                  <CardDescription>Medicare eligibility criteria reference</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px]">
                    {lcdCriteria ? (
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-sm mb-2">General Decline Indicators</h4>
                          <p className="text-xs text-muted-foreground mb-2">
                            At least {lcdCriteria.general_decline?.required_count || 3} indicators required
                          </p>
                          <ul className="text-xs space-y-1">
                            {lcdCriteria.general_decline?.indicators?.slice(0, 5).map((ind, i) => (
                              <li key={i} className="flex items-start gap-1">
                                <CheckCircle2 className="h-3 w-3 mt-0.5 text-teal-600 flex-shrink-0" />
                                <span>{ind.description}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <Separator />
                        <div>
                          <h4 className="font-medium text-sm mb-2">Disease Categories</h4>
                          <div className="flex flex-wrap gap-1">
                            {Object.keys(lcdCriteria.disease_specific || {}).map((key) => (
                              <Badge key={key} variant="outline" className="text-xs">
                                {lcdCriteria.disease_specific[key].name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground">Loading criteria...</p>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Document Upload Tab */}
          <TabsContent value="documents" className="space-y-4" data-testid="documents-tab">
            <Card className="bg-white/90 backdrop-blur">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileUp className="h-5 w-5 text-blue-600" />
                  Upload Hospital Records
                </CardTitle>
                <CardDescription>
                  Upload PDF, images (including scanned documents), DOCX, or TXT files for automatic OCR extraction
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Referral ID *</Label>
                    <Input
                      placeholder="Enter referral ID"
                      value={selectedReferralId}
                      onChange={(e) => setSelectedReferralId(e.target.value)}
                      data-testid="referral-id-input"
                    />
                  </div>
                  <div>
                    <Label>Patient Name *</Label>
                    <Input
                      placeholder="Patient full name"
                      value={patientName}
                      onChange={(e) => setPatientName(e.target.value)}
                      data-testid="patient-name-input"
                    />
                  </div>
                </div>
                
                <div className="border-2 border-dashed rounded-lg p-8 text-center">
                  <input
                    type="file"
                    id="document-upload"
                    className="hidden"
                    accept=".pdf,.png,.jpg,.jpeg,.tiff,.docx,.txt"
                    onChange={handleFileUpload}
                    disabled={uploadingFile}
                    data-testid="file-input"
                  />
                  <label 
                    htmlFor="document-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    {uploadingFile ? (
                      <>
                        <Loader2 className="h-12 w-12 text-teal-600 animate-spin mb-3" />
                        <p className="font-medium">Uploading & Processing...</p>
                        <Progress value={uploadProgress} className="w-48 mt-2" />
                      </>
                    ) : (
                      <>
                        <Upload className="h-12 w-12 text-teal-600 mb-3" />
                        <p className="font-medium">Click to upload or drag and drop</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          PDF, PNG, JPG, TIFF, DOCX, TXT (max 50MB)
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Includes OCR for scanned documents
                        </p>
                      </>
                    )}
                  </label>
                </div>
                
                {referralDocuments.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-medium mb-3">Uploaded Documents</h4>
                    <div className="space-y-2">
                      {referralDocuments.map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-blue-600" />
                            <div>
                              <p className="font-medium text-sm">{doc.original_filename}</p>
                              <p className="text-xs text-muted-foreground">
                                {doc.extraction_status === "complete" ? (
                                  <span className="text-green-600">✓ Extracted ({Math.round((doc.ocr_confidence || 0) * 100)}% confidence)</span>
                                ) : doc.extraction_status === "processing" ? (
                                  <span className="text-blue-600">Processing...</span>
                                ) : (
                                  <span className="text-red-600">Extraction failed</span>
                                )}
                              </p>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* E-Signatures Tab */}
          <TabsContent value="signatures" className="space-y-4" data-testid="signatures-tab">
            <Card className="bg-white/90 backdrop-blur">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSignature className="h-5 w-5 text-indigo-600" />
                  Documents Awaiting Your Signature
                </CardTitle>
                <CardDescription>
                  Encrypted electronic signatures with full audit trail
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pendingSignatures.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500/50" />
                    <p>No documents awaiting your signature</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingSignatures.map((pkg) => (
                      <div 
                        key={pkg.id}
                        className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                        data-testid={`signature-${pkg.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{pkg.document_title}</p>
                            <p className="text-sm text-muted-foreground">
                              Patient: {pkg.patient_name} • Type: {pkg.document_type.replace('_', ' ')}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Expires: {new Date(pkg.expires_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={SIGNATURE_STATUS[pkg.status]?.color || "bg-gray-100"}>
                              {SIGNATURE_STATUS[pkg.status]?.label || pkg.status}
                            </Badge>
                            <Button 
                              size="sm"
                              onClick={() => {
                                setSigningPackage(pkg);
                                setShowSignDialog(true);
                              }}
                            >
                              <Pen className="h-4 w-4 mr-1" />
                              Sign
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Portal Integrations Tab */}
          <TabsContent value="portals" className="space-y-4" data-testid="portals-tab">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Integration Status */}
              <Card className="bg-white/90 backdrop-blur">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Link2 className="h-5 w-5 text-teal-600" />
                    Portal Integrations
                  </CardTitle>
                  <CardDescription>
                    CarePort, AIDIN, and other referral portal connections
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {["careport", "aidin", "navihealth", "epic"].map((portal) => {
                      const integration = portalIntegrations.find(p => p.portal_type === portal);
                      return (
                        <div 
                          key={portal}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${integration?.status === "active" ? "bg-green-500" : "bg-gray-300"}`} />
                            <div>
                              <p className="font-medium capitalize">{portal}</p>
                              <p className="text-xs text-muted-foreground">
                                {integration ? `Last sync: ${integration.last_sync ? new Date(integration.last_sync).toLocaleString() : "Never"}` : "Not configured"}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {integration?.status === "active" && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleSyncPortal(portal)}
                              >
                                <RefreshCw className="h-3 w-3 mr-1" />
                                Sync
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setShowConfigPortal(true)}
                            >
                              Configure
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Inbound Referrals */}
              <Card className="bg-white/90 backdrop-blur">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ExternalLink className="h-5 w-5 text-green-600" />
                    Inbound Referrals
                  </CardTitle>
                  <CardDescription>
                    Referrals received from external portals
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    {inboundReferrals.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p>No inbound referrals</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {inboundReferrals.map((ref) => (
                          <div 
                            key={ref.id}
                            className="p-3 border rounded-lg"
                            data-testid={`inbound-${ref.id}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <p className="font-medium">{ref.patient_name}</p>
                                <p className="text-sm text-muted-foreground">{ref.primary_diagnosis}</p>
                              </div>
                              <Badge variant="outline" className="capitalize">
                                {ref.source_portal}
                              </Badge>
                            </div>
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                              <span>From: {ref.referring_facility || "Unknown"}</span>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleImportReferral(ref.id)}
                              >
                                Import
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Assessment Detail Dialog */}
      <Dialog open={showAssessmentDetail} onOpenChange={setShowAssessmentDetail}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedAssessment && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ClipboardCheck className="h-5 w-5" />
                  Eligibility Assessment Review
                </DialogTitle>
                <DialogDescription>
                  {selectedAssessment.patient_name} - {selectedAssessment.primary_diagnosis}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Status Banner */}
                <Alert className={ELIGIBILITY_STATUS[selectedAssessment.eligibility_status]?.color || ""}>
                  <AlertDescription className="flex items-center justify-between">
                    <span className="font-medium">
                      Status: {ELIGIBILITY_STATUS[selectedAssessment.eligibility_status]?.label}
                    </span>
                    <span>Confidence: {Math.round(selectedAssessment.confidence_score * 100)}%</span>
                  </AlertDescription>
                </Alert>
                
                {/* General Decline */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm">General Decline Indicators</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedAssessment.general_decline?.met_indicators?.map((ind, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <span>{ind.id.replace('_', ' ')}: <strong>{ind.value}</strong></span>
                        </div>
                      ))}
                      {selectedAssessment.general_decline?.met_indicators?.length === 0 && (
                        <p className="text-muted-foreground text-sm">No indicators met</p>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {selectedAssessment.general_decline?.indicator_count || 0} of 3 required indicators met
                    </p>
                  </CardContent>
                </Card>
                
                {/* Disease Specific */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm">
                      Disease-Specific Criteria ({selectedAssessment.disease_specific?.category_name})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedAssessment.disease_specific?.met_criteria?.map((crit, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <span>{crit.id.replace(/_/g, ' ')}: <strong>{crit.value}</strong></span>
                        </div>
                      ))}
                      {selectedAssessment.disease_specific?.met_criteria?.length === 0 && (
                        <p className="text-muted-foreground text-sm">No criteria met</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Writeup */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm">Eligibility Narrative</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[200px]">
                      <pre className="text-xs whitespace-pre-wrap font-sans">
                        {selectedAssessment.writeup}
                      </pre>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
              
              <DialogFooter className="flex-col sm:flex-row gap-2">
                {selectedAssessment.status === "pending_review" && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => handleApproveAssessment(selectedAssessment.id, false, "Does not meet LCD criteria")}
                      className="text-red-600"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                    <Button
                      onClick={() => handleApproveAssessment(selectedAssessment.id, true)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                  </>
                )}
                {selectedAssessment.approval_status === "approved" && !selectedAssessment.cti_id && (
                  <Button onClick={() => handleGenerateCTI(selectedAssessment.id)}>
                    <FileText className="h-4 w-4 mr-2" />
                    Generate CTI
                  </Button>
                )}
                {selectedAssessment.cti_id && (
                  <Button variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Download CTI
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* New Assessment Dialog */}
      <Dialog open={showNewAssessment} onOpenChange={setShowNewAssessment}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Eligibility Assessment</DialogTitle>
            <DialogDescription>
              Enter patient clinical data to assess hospice eligibility against CGS LCD criteria
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Referral ID *</Label>
                <Input
                  value={newAssessmentData.referral_id}
                  onChange={(e) => setNewAssessmentData({...newAssessmentData, referral_id: e.target.value})}
                  placeholder="REF-12345"
                  data-testid="new-referral-id"
                />
              </div>
              <div>
                <Label>Patient Name *</Label>
                <Input
                  value={newAssessmentData.patient_name}
                  onChange={(e) => setNewAssessmentData({...newAssessmentData, patient_name: e.target.value})}
                  placeholder="John Doe"
                  data-testid="new-patient-name"
                />
              </div>
            </div>
            
            <div>
              <Label>Primary Diagnosis *</Label>
              <Input
                value={newAssessmentData.primary_diagnosis}
                onChange={(e) => setNewAssessmentData({...newAssessmentData, primary_diagnosis: e.target.value})}
                placeholder="e.g., End-stage heart failure"
                data-testid="new-diagnosis"
              />
            </div>
            
            <div>
              <Label>ICD-10 Codes (comma-separated)</Label>
              <Input
                value={newAssessmentData.icd10_codes}
                onChange={(e) => setNewAssessmentData({...newAssessmentData, icd10_codes: e.target.value})}
                placeholder="I50.9, I25.10"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Karnofsky Score (0-100)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={newAssessmentData.karnofsky_score}
                  onChange={(e) => setNewAssessmentData({...newAssessmentData, karnofsky_score: e.target.value})}
                  placeholder="50"
                />
              </div>
              <div>
                <Label>Weight Loss %</Label>
                <Input
                  type="number"
                  value={newAssessmentData.weight_loss_percent}
                  onChange={(e) => setNewAssessmentData({...newAssessmentData, weight_loss_percent: e.target.value})}
                  placeholder="10"
                />
              </div>
              <div>
                <Label>Albumin (g/dL)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={newAssessmentData.albumin}
                  onChange={(e) => setNewAssessmentData({...newAssessmentData, albumin: e.target.value})}
                  placeholder="2.5"
                />
              </div>
            </div>
            
            <div>
              <Label className="mb-2 block">ADL Dependencies</Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "adl_bathing", label: "Bathing" },
                  { key: "adl_dressing", label: "Dressing" },
                  { key: "adl_feeding", label: "Feeding" },
                  { key: "adl_transfers", label: "Transfers" },
                  { key: "adl_continence", label: "Continence" },
                  { key: "adl_ambulation", label: "Ambulation" }
                ].map((adl) => (
                  <div key={adl.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={adl.key}
                      checked={newAssessmentData[adl.key]}
                      onCheckedChange={(checked) => setNewAssessmentData({...newAssessmentData, [adl.key]: checked})}
                    />
                    <Label htmlFor={adl.key} className="text-sm">{adl.label}</Label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="disease_progression"
                checked={newAssessmentData.disease_progression}
                onCheckedChange={(checked) => setNewAssessmentData({...newAssessmentData, disease_progression: checked})}
              />
              <Label htmlFor="disease_progression">Disease progression despite treatment</Label>
            </div>
            
            <div>
              <Label>Recent Hospitalizations (last 6 months)</Label>
              <Input
                type="number"
                min="0"
                value={newAssessmentData.recent_hospitalizations}
                onChange={(e) => setNewAssessmentData({...newAssessmentData, recent_hospitalizations: e.target.value})}
                placeholder="2"
              />
            </div>
          </div>
          
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleRunAssessment} data-testid="run-assessment-btn">
              <Brain className="h-4 w-4 mr-2" />
              Run Assessment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sign Document Dialog */}
      <Dialog open={showSignDialog} onOpenChange={setShowSignDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="h-5 w-5" />
              Sign Document
            </DialogTitle>
            <DialogDescription>
              {signingPackage?.document_title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertDescription>
                Your signature will be encrypted and stored with a cryptographic hash for audit purposes.
              </AlertDescription>
            </Alert>
            
            <div>
              <Label>Type your full legal name to sign</Label>
              <Input
                value={signatureInput}
                onChange={(e) => setSignatureInput(e.target.value)}
                placeholder="John Doe, MD"
                className="mt-1"
                data-testid="signature-input"
              />
            </div>
            
            <div className="p-4 border rounded-lg text-center">
              <p className="font-script text-2xl italic">{signatureInput || "Your signature will appear here"}</p>
            </div>
            
            <p className="text-xs text-muted-foreground">
              By signing, you agree: "I agree to sign this document electronically and understand this signature has the same legal effect as a handwritten signature."
            </p>
          </div>
          
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleSignDocument} disabled={!signatureInput.trim()} data-testid="submit-signature-btn">
              <Pen className="h-4 w-4 mr-2" />
              Sign Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
